/**
 * StoreHero 同時購入記録のカート同期
 *
 * storehero_cross-sell(-modal) セクションは、ギフト等の同時購入商品がカートに
 * 追加されたとき cart attributes に「{追加商品} → {メイン商品}」の記録を追記する。
 * この記録は追記専用のため、カートから商品を削除しても残り続け、注文データに
 * 実際には購入されていない組み合わせが混入する。
 *
 * このスクリプトはカートが変化したタイミングで記録を現在のカート内容と突き合わせ、
 * カートに存在しない商品を含む組み合わせを取り除く。
 *
 * 判定を誤って有効な記録を消すことを避けるため、照合は寛容側に倒している
 * （どのカート商品にも対応付けられない組み合わせだけを削除する）。
 *
 * ATTRIBUTE_KEYS はセクション設定 cart_attr_key と一致させる必要がある。
 * カートページではセクション設定を参照できないため、ここで明示的に列挙している。
 */
(function () {
  const ATTRIBUTE_KEYS = ['Gift Wrapping', 'Gift Wrappinh'];
  const SEPARATOR = '、';
  const ARROW = ' → ';

  // 商品の追加では既存の組み合わせが無効になることはないため同期の対象外。
  // モーダル側の記録書き込みと衝突させないためでもある（cartUpdate の発行元で判定する）。
  const ADD_ONLY_SOURCES = ['product-form', 'quick-add'];

  let running = false;
  let pending = false;

  /** cartUpdate が渡してくるデータがカート全体として使えるか */
  const isFullCart = (data) =>
    !!data && Array.isArray(data.items) && Object.prototype.hasOwnProperty.call(data, 'attributes');

  /** 追加商品側のラベルがカート内の商品と対応するか（title / handle / id の各形式に対応） */
  const matchesAddedItem = (label, items) =>
    items.some(
      (item) =>
        label === item.title ||
        label === item.product_title ||
        label === item.handle ||
        label === `${item.handle}(${item.variant_id})` ||
        label === String(item.product_id) ||
        label === `${item.product_id}:${item.variant_id}`
    );

  /** メイン商品側のラベルがカート内の商品と対応するか */
  const matchesSourceItem = (label, items) =>
    items.some(
      (item) =>
        label === item.product_title ||
        label === item.title ||
        label === item.handle ||
        label === String(item.product_id)
    );

  const reconcile = async (event) => {
    if (event && ADD_ONLY_SOURCES.includes(event.source)) return;

    // 実行中に別のカート変化が起きた場合は取りこぼさないよう、完了後に再実行する
    if (running) {
      pending = true;
      return;
    }
    running = true;

    try {
      // cartUpdate がカート全体を渡してくる場合はそれを使い、余分なリクエストと
      // チェックアウト遷移との競合の余地を減らす。使えない場合のみ取得する。
      // パスは storehero_cross-sell(-modal) セクション側の書き込み処理と同じ形式に揃えている
      const cart = isFullCart(event && event.cartData)
        ? event.cartData
        : await fetch('/cart.js').then((res) => res.json());
      const items = cart.items || [];
      const updates = {};

      ATTRIBUTE_KEYS.forEach((key) => {
        const value = (cart.attributes || {})[key];
        if (!value) return;

        const kept = value
          .split(SEPARATOR)
          .map((entry) => entry.trim())
          .filter(Boolean)
          .filter((entry) => {
            const index = entry.indexOf(ARROW);
            if (index < 0) return true; // 想定外の形式は判断せず残す
            const added = entry.slice(0, index).trim();
            const source = entry.slice(index + ARROW.length).trim();
            return matchesAddedItem(added, items) && matchesSourceItem(source, items);
          });

        const newValue = kept.join(SEPARATOR);
        if (newValue !== value) updates[key] = newValue;
      });

      if (Object.keys(updates).length === 0) return;

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: updates }),
        // 削除直後にチェックアウトへ遷移しても書き込みを打ち切られないようにする
        keepalive: true,
      });
    } catch (error) {
      console.error('Cross-sell cart attributes sync failed:', error);
    } finally {
      running = false;
      if (pending) {
        pending = false;
        reconcile();
      }
    }
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, reconcile);
  }

  // 本スクリプト導入前に記録が残ったカートを救うため、カートページの初回表示時にも実行する
  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('cart-items')) reconcile();
  });
})();
